import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus, DollarSign, TrendingUp, Calendar, Edit, Trash2,
  Power, PowerOff, PieChart, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BurnRateManager = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Labor',
    amount: '',
    frequency: 'Monthly',
    description: '',
    is_active: true
  });

  const categories = ['All', 'Labor', 'Rent', 'Utilities', 'Insurance', 'Equipment', 'Consumables', 'Maintenance', 'Marketing', 'Other'];
  const frequencies = ['Monthly', 'Quarterly', 'Annual', 'One-time'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/burn-rate`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/burn-rate/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setItems(itemsRes.data.items || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load burn rate data:', error);
      toast.error('Failed to load burn rate data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        category: 'Labor',
        amount: '',
        frequency: 'Monthly',
        description: '',
        is_active: true
      });
    }
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingItem) {
        await axios.put(`${API}/burn-rate/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Burn rate item updated successfully!');
      } else {
        await axios.post(`${API}/burn-rate`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Burn rate item created successfully!');
      }

      setShowFormModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to save burn rate item:', error);
      toast.error('Failed to save burn rate item');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/burn-rate/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Burn rate item deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete burn rate item:', error);
      toast.error('Failed to delete burn rate item');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/burn-rate/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      loadData();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredItems = filterCategory === 'All'
    ? items
    : items.filter(item => item.category === filterCategory);

  const getCategoryColor = (category) => {
    const colors = {
      Labor: 'bg-blue-500/20 text-blue-400',
      Rent: 'bg-purple-500/20 text-purple-400',
      Utilities: 'bg-yellow-500/20 text-yellow-400',
      Insurance: 'bg-red-500/20 text-red-400',
      Equipment: 'bg-green-500/20 text-green-400',
      Consumables: 'bg-orange-500/20 text-orange-400',
      Maintenance: 'bg-pink-500/20 text-pink-400',
      Marketing: 'bg-indigo-500/20 text-indigo-400',
      Other: 'bg-gray-500/20 text-gray-400'
    };
    return colors[category] || colors.Other;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase text-gray-900">
            Burn Rate Manager
          </h1>
          <p className="text-gray-700 mt-1 font-medium">
            Track all operational costs and overhead expenses
          </p>
        </div>
        <Button onClick={() => handleOpenForm()}>
          <Plus size={16} className="mr-2" />
          Add Expense
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-gray-700 mb-1">Monthly Burn</p>
                  <p className="text-3xl font-bold text-blue-600">
                    ${summary.total_monthly.toLocaleString()}
                  </p>
                </div>
                <Calendar size={32} className="text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-gray-700 mb-1">Quarterly Burn</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${summary.total_quarterly.toLocaleString()}
                  </p>
                </div>
                <BarChart3 size={32} className="text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-gray-700 mb-1">Annual Burn</p>
                  <p className="text-3xl font-bold text-purple-600">
                    ${summary.total_annual.toLocaleString()}
                  </p>
                </div>
                <TrendingUp size={32} className="text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-gray-700 mb-1">Active Items</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {summary.items_count}
                  </p>
                </div>
                <DollarSign size={32} className="text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && Object.keys(summary.category_breakdown).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <PieChart size={20} />
              Category Breakdown (Monthly)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(summary.category_breakdown).map(([category, amount]) => (
                <div key={category} className="p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoryColor(category)}`}>
                      {category}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      ${amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-semibold text-gray-900">Filter by Category:</Label>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterCategory !== 'All' && (
          <Badge variant="secondary">{filteredItems.length} items</Badge>
        )}
      </div>

      {/* Items List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-700">
            Loading burn rate items...
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-900 mb-2 font-semibold text-lg">No expenses found</p>
            <p className="text-sm text-gray-700 mb-4">
              Add your first operational expense to start tracking burn rate
            </p>
            <Button onClick={() => handleOpenForm()}>
              <Plus size={16} className="mr-2" />
              Add First Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 rounded-lg border-2 ${
                    item.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                        <Badge className={getCategoryColor(item.category)}>
                          {item.category}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {item.frequency}
                        </Badge>
                        {!item.is_active && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                      )}
                      <p className="text-2xl font-bold text-gray-900">
                        ${item.amount.toLocaleString()}
                        <span className="text-sm text-gray-600 font-normal ml-2">
                          / {item.frequency}
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(item.id)}
                        className={item.is_active ? 'text-green-600' : 'text-gray-400'}
                        title={item.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {item.is_active ? <Power size={16} /> : <PowerOff size={16} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenForm(item)}
                        className="text-blue-500"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(item.id, item.name)}
                        className="text-red-500"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Costing Integration Insight */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-900">💡 Costing Setup Integration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-900 mb-3">
            Your total monthly burn rate of <strong>${summary?.total_monthly.toLocaleString()}</strong> should be factored into your overhead calculations.
          </p>
          <div className="space-y-2 text-sm text-blue-800">
            <p>✓ This includes all Labor, Rent, Utilities, and operational expenses</p>
            <p>✓ Use this data to calculate your shop overhead multiplier in Costing Setup</p>
            <p>✓ Recommended: Add 15-25% markup on top of burn rate for profit margin</p>
          </div>
        </CardContent>
      </Card>

      {/* Form Modal */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-xl">
              {editingItem ? 'Edit Expense Item' : 'Add Expense Item'}
            </DialogTitle>
            <DialogDescription className="text-gray-700">
              Track operational costs and overhead expenses
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm font-semibold text-gray-900">Expense Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Shop Rent, Electricity Bill"
                  required
                  className="text-gray-900"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-900">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c !== 'All').map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-900">Frequency *</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                >
                  <SelectTrigger className="text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq} value={freq}>
                        {freq}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label className="text-sm font-semibold text-gray-900">Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  className="text-gray-900"
                />
              </div>

              <div className="col-span-2">
                <Label className="text-sm font-semibold text-gray-900">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional notes about this expense..."
                  rows={3}
                  className="text-gray-900"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFormModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingItem ? 'Update' : 'Add'} Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BurnRateManager;
